import { Link } from 'react-router-dom';
import { categories, featuredVideos, hotVideos, articles } from '@/mocks/videos';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const categoryNames: Record<string, string> = {
  dianshi: '电视剧',
  dianying: '电影',
  zongyi: '综艺',
  dongman: '动漫',
  jilupian: '纪录片',
  duanju: '短剧',
  tiyu: '体育',
  yinyue: '音乐',
};

export default function HomePage() {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: '91tv是什么平台？', acceptedAnswer: { '@type': 'Answer', text: '91tv是一个高清视频资源聚合平台，提供海量影视、综艺、动漫及短视频资源，支持多终端流畅播放，为用户提供极速、清晰、免费的在线观看体验。' } },
      { '@type': 'Question', name: '91tv是否免费？', acceptedAnswer: { '@type': 'Answer', text: '是的，91tv平台上的所有视频内容均可免费在线观看，无需付费订阅。用户只需访问91tv官网即可开始观看。' } },
      { '@type': 'Question', name: '91tv支持哪些播放设备？', acceptedAnswer: { '@type': 'Answer', text: '91tv支持PC、平板、手机等多种终端设备，无论在电脑上还是移动设备上，都能获得流畅的高清观看体验。' } },
      { '@type': 'Question', name: '如何找到91tv最新地址？', acceptedAnswer: { '@type': 'Answer', text: '建议收藏91tv官方网站链接，或关注91tv官方社交媒体账号获取最新地址更新信息。' } },
      { '@type': 'Question', name: '91tv视频画质如何？', acceptedAnswer: { '@type': 'Answer', text: '91tv提供高清视频画质，大部分视频支持1080P甚至4K超清播放，根据用户网络情况自动适配最佳画质。' } },
    ],
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '91tv',
    url: 'https://www.91tv.com',
    description: '91tv是深受用户喜爱的高清视频聚合平台，提供海量影视、综艺、动漫及短视频资源。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.91tv.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '91tv',
    url: 'https://www.91tv.com',
    description: '91tv高清视频资源聚合平台',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-400-888-9191',
      contactType: 'customer service',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="relative w-full min-h-[580px] md:min-h-[680px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Dark%20cinematic%20theater%20interior%20with%20glowing%20neon%20light%20streaks%20in%20warm%20orange%20and%20amber%20tones%2C%20abstract%20geometric%20shapes%2C%20modern%20digital%20entertainment%20atmosphere%2C%20luxurious%20yet%20minimalist%20design%2C%20soft%20gradient%20shadows%2C%20professional%20broadcast%20studio%20vibe&width=1920&height=1080&seq=hero-bg-91tv&orientation=landscape"
              alt="91tv高清视频平台"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60"></div>
          </div>

          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <div className="max-w-[800px] mx-auto">
              <p className="text-primary-400 text-sm md:text-base font-label tracking-widest mb-4 animate-[fadeInUp_0.6s_ease-out]">
                高清视频资源一站聚合
              </p>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white font-heading leading-tight mb-6 animate-[fadeInUp_0.6s_ease-out_0.1s]">
                发现精彩，畅享<span className="text-primary-400">极速观影</span>体验
              </h1>
              <p className="text-white/70 text-sm md:text-lg max-w-[600px] mx-auto mb-8 leading-relaxed animate-[fadeInUp_0.6s_ease-out_0.2s]">
                海量影视、综艺、动漫及短视频资源，支持多终端流畅播放。<br className="hidden sm:block" />
                立即访问91tv官方入口，开启你的高清观影之旅。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-[fadeInUp_0.6s_ease-out_0.3s]">
                <a href="#featured" className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap text-sm">
                  <i className="ri-play-circle-line mr-2"></i>开始观看
                </a>
                <Link to="/about" className="px-8 py-3 border border-white/30 hover:border-white/60 text-white rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap text-sm">
                  了解更多
                </Link>
              </div>
            </div>
          </div>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <a href="#categories" className="text-white/50 hover:text-white/80 transition-colors cursor-pointer">
              <i className="ri-arrow-down-s-line text-2xl"></i>
            </a>
          </div>
        </section>

        {/* Category Navigation */}
        <section id="categories" className="py-14 md:py-20 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                视频分类
              </h2>
              <p className="text-foreground-500 mt-3 text-sm">选择你感兴趣的内容类型，发现更多精彩视频</p>
            </div>
            <div className="grid grid-cols-4 md:grid-cols-8 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center gap-3 p-4 md:p-5 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 hover:bg-primary-50/50 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-gradient-to-br from-primary-500/10 to-accent-500/10 group-hover:from-primary-500/20 group-hover:to-accent-500/20 transition-all duration-300">
                    <i className={`${cat.icon} text-xl md:text-2xl text-primary-500 group-hover:scale-110 transition-transform duration-300`}></i>
                  </div>
                  <span className="text-xs md:text-sm text-foreground-700 group-hover:text-primary-600 font-medium transition-colors">
                    {cat.name}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Videos */}
        <section id="featured" className="py-14 md:py-20 bg-background-100">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                  精选推荐
                </h2>
                <p className="text-foreground-500 mt-2 text-sm">编辑精选高清内容，不容错过</p>
              </div>
              <Link to="/category/dianshi" className="text-sm text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap">
                查看更多 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {featuredVideos.map((video) => (
                <Link
                  key={video.id}
                  to={`/article/${video.id}`}
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300"></div>
                    <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-md">
                      {video.duration}
                    </div>
                    <div className="absolute top-2 left-2 flex gap-1">
                      {video.tags.map((tag) => (
                        <span key={tag} className="bg-primary-500/90 text-white text-xs px-2 py-0.5 rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/90">
                        <i className="ri-play-fill text-white text-xl ml-0.5"></i>
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                      {video.title}
                    </h3>
                    <div className="flex items-center gap-3 mt-2 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {video.views >= 10000 ? `${(video.views / 10000).toFixed(0)}万` : video.views}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-star-fill text-amber-400"></i>
                        {video.rating}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Ranking */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                  热门排行
                </h2>
                <p className="text-foreground-500 mt-2 text-sm">全网热播视频实时排行</p>
              </div>
              <div className="flex gap-2">
                {['日榜', '周榜', '月榜'].map((tab) => (
                  <button
                    key={tab}
                    className={`px-4 py-1.5 text-sm rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                      tab === '周榜'
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {hotVideos.map((video) => (
                <Link
                  key={video.id}
                  to={`/article/${video.id}`}
                  className="group flex gap-3 bg-background-100/70 rounded-xl p-3 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative flex-shrink-0 w-24 h-16 rounded-lg overflow-hidden">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500/90">
                        <i className="ri-play-fill text-white text-sm ml-0.5"></i>
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2">
                      <span className={`flex-shrink-0 text-base font-bold font-label ${
                        video.rank <= 3 ? 'text-primary-500' : 'text-foreground-400'
                      }`}>
                        {video.rank.toString().padStart(2, '0')}
                      </span>
                      <div className="min-w-0">
                        <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                          {video.title}
                        </h3>
                        <div className="flex items-center gap-2 mt-1.5 text-xs text-foreground-500">
                          <span>{categoryNames[video.category]}</span>
                          <span>·</span>
                          <span className="flex items-center gap-0.5">
                            <i className="ri-star-fill text-amber-400 text-xs"></i>
                            {video.rating}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Articles */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                  最新资讯
                </h2>
                <p className="text-foreground-500 mt-2 text-sm">影视动态、观影指南、平台攻略</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {articles.slice(0, 3).map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={article.thumbnail}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs bg-accent-100 text-accent-700 px-2 py-0.5 rounded-full">
                        {categoryNames[article.category] || article.category}
                      </span>
                      <span className="text-xs text-foreground-500">{article.publishDate}</span>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-2 line-clamp-2 leading-relaxed">
                      {article.summary}
                    </p>
                    <div className="flex items-center gap-3 mt-3 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {article.views >= 10000 ? `${(article.views / 10000).toFixed(0)}万` : article.views}
                      </span>
                      <span>{article.author}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/category/zongyi"
                className="inline-flex items-center gap-2 px-6 py-2.5 border border-background-300 rounded-full text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
              >
                查看全部资讯 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Stats / Trust Section */}
        <section className="py-14 md:py-16 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {[
                { value: '5000万+', label: '月活跃用户' },
                { value: '10万+', label: '视频资源' },
                { value: '99.9%', label: '服务可用率' },
                { value: '4.9', label: '用户评分' },
              ].map((stat) => (
                <div key={stat.label} className="p-6 rounded-xl bg-background-100/70 border border-background-200/70">
                  <div className="text-2xl md:text-3xl font-bold text-primary-500 font-label">{stat.value}</div>
                  <div className="text-sm text-foreground-600 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-[900px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                常见问题
              </h2>
              <p className="text-foreground-500 mt-2 text-sm">关于91tv，你想知道的都在这里</p>
            </div>
            <div className="space-y-3">
              {faqSchema.mainEntity.map((faq, idx) => (
                <details key={idx} className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
                  <summary className="flex items-center justify-between p-5 cursor-pointer hover:bg-background-100 transition-colors">
                    <span className="text-sm font-medium text-foreground-800 pr-4">{faq.name}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-300 flex-shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">
                    {faq.acceptedAnswer.text}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-[800px] mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-4">
              立即加入91tv，开启高清观影之旅
            </h2>
            <p className="text-foreground-500 text-sm mb-8">
              海量高清视频资源，随时随地免费观看
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="#featured" className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap text-sm">
                <i className="ri-play-circle-line mr-2"></i>立即观看
              </a>
              <Link to="/about" className="px-8 py-3 border border-background-300 rounded-full text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                了解91tv
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
}